import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Phone, MessageSquare, Flame, UserPlus, Users, Clock, Edit3, DollarSign, UserCheck, Globe, Shield, Tag, Snowflake } from "lucide-react";
import EditClientModal from "@/components/EditClientModal";

export default function ClientDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callModal, setCallModal] = useState(false);
  const [callResponse, setCallResponse] = useState({ response: "", notes: "" });
  const [calling, setCalling] = useState(false);
  const [convertModal, setConvertModal] = useState(false);
  const [convertData, setConvertData] = useState({ deposit_amount: "", deposit_date: "" });
  const [editModalOpen, setEditModalOpen] = useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      const { data } = await apiCall("get", `/clients/${userId}`);
      setClientData(data);
    } catch (err) {
      toast.error("Client not found");
      navigate("/clients");
    } finally { setLoading(false); }
  }, [userId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCall = async () => {
    setCalling(true);
    try {
      const { data } = await apiCall("post", "/bonvoice/click2call", {
        client_user_id: clientData.client.user_id,
        destination: clientData.client.mobile_masked ? "MASKED" : clientData.client.mobile_no
      });
      toast.success("Call initiated!");
      setCallModal(true);
      setCallResponse({ response: "", notes: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Call failed"); }
    finally { setCalling(false); }
  };

  const submitResponse = async () => {
    if (!callResponse.response) { toast.error("Select a response"); return; }
    if (callResponse.response === "converted") { setConvertModal(true); return; }
    try {
      await apiCall("post", "/call-response", { client_id: clientData.client.user_id, call_id: "", response: callResponse.response, notes: callResponse.notes });
      toast.success("Response saved");
      setCallModal(false);
      setCallResponse({ response: "", notes: "" });
      fetchData();
    } catch { toast.error("Failed to save"); }
  };

  const submitConversion = async () => {
    if (!convertData.deposit_amount || !convertData.deposit_date) { toast.error("Enter deposit details"); return; }
    try {
      await apiCall("post", "/call-response", { client_id: clientData.client.user_id, call_id: "", response: "converted", notes: callResponse.notes });
      await apiCall("post", "/clients/convert", { client_id: clientData.client.user_id, deposit_amount: convertData.deposit_amount, deposit_date: convertData.deposit_date });
      toast.success("Conversion submitted for admin approval");
      setConvertModal(false);
      setCallModal(false);
      setCallResponse({ response: "", notes: "" });
      setConvertData({ deposit_amount: "", deposit_date: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const [waModal, setWaModal] = useState(false);
  const [waMsg, setWaMsg] = useState("Hello! Thank you for being our valued customer.");

  const handleWhatsApp = () => setWaModal(true);

  const sendWhatsApp = async () => {
    try {
      await apiCall("post", "/whatsapp/send-wati", { client_user_id: clientData.client.user_id, message: waMsg });
      toast.success("WhatsApp message sent!");
      setWaModal(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!clientData) return null;

  const getCatBadge = (cat) => {
    if (cat === "fresh" || cat === "new") return <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"><UserPlus className="w-3 h-3 mr-1" />Fresh</Badge>;
    if (cat === "hot") return <Badge className="bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20"><Flame className="w-3 h-3 mr-1" />Hot</Badge>;
    if (cat === "cold") return <Badge className="bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20"><Snowflake className="w-3 h-3 mr-1" />Cold</Badge>;
    return <Badge variant="secondary"><Users className="w-3 h-3 mr-1" />Regular</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate("/clients")} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Client: <button className="text-primary hover:underline" onClick={() => {navigator.clipboard.writeText(`${client.user_id}`); toast.success("Copied");}}>{client.user_id}</button></h2>
            {client.username && <span className="text-sm font-medium text-muted-foreground">({client.username})</span>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            {getCatBadge(client.category)}
            {client.site && <Badge variant="outline" className="text-xs">{client.site}</Badge>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={() => setEditModalOpen(true)} className="border-primary/30 hover:bg-primary/5">
              <Edit3 className="w-4 h-4 mr-2 text-primary" /> Edit Client
            </Button>
          )}
          <Button onClick={handleCall} disabled={calling} data-testid="detail-call-btn">
            <Phone className="w-4 h-4 mr-2" /> Call
          </Button>
          {(user?.role === "admin" || user?.whatsapp_enabled) && (
            <Button variant="outline" onClick={handleWhatsApp} data-testid="detail-whatsapp-btn">
              <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Contact & Identity Card */}
        <Card className="border border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Identity & Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2.5">
            <div className="space-y-2">
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">User ID</span><span className="text-xs font-mono font-medium text-primary cursor-pointer hover:underline" onClick={() => {navigator.clipboard.writeText(`${client.user_id}`); toast.success("Copied");}}>{client.user_id}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Username / Name</span><span className="text-xs font-medium">{client.username || client.client_name || "-"}</span></div>
              {!client.mobile_masked && <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Mobile</span><span className="text-xs font-mono font-medium">{client.mobile_no || "-"}</span></div>}
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Registered Date</span><span className="text-xs">{client.registration_date || "N/A"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Branch</span><span className="text-xs font-medium">{client.branch || "-"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Site Name</span><span className="text-xs font-medium">{client.site || "-"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Source</span><Badge variant="secondary" className="text-[10px] uppercase">{client.source || "manual"}</Badge></div>
            </div>
          </CardContent>
        </Card>

        {/* Deposit & Financial Info Card */}
        <Card className="border border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Financial Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2.5">
            <div className="space-y-2">
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Total Deposit</span><span className="text-xs font-mono font-bold text-green-600">₹{Number(client.total_deposit || 0).toLocaleString()}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Total Withdrawal</span><span className="text-xs font-mono font-medium text-red-500">₹{Number(client.total_withdraw || 0).toLocaleString()}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Deposit Count</span><span className="text-xs font-medium">{client.deposit_count || 0}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Last Deposit</span><span className="text-xs font-medium">{client.last_deposit ? client.last_deposit.split(" ")[0] : "Never"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Last Withdrawal</span><span className="text-xs font-medium">{client.last_withdraw ? client.last_withdraw.split(" ")[0] : "Never"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">First Deposit</span><span className="text-xs">{client.first_deposit_amount ? `₹${client.first_deposit_amount} (${client.first_deposit_date || '-'})` : "-"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Category</span>{getCatBadge(client.category)}</div>
            </div>
          </CardContent>
        </Card>

        {/* Assignment & Call Summary Card */}
        <Card className="border border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5" /> Assignment & Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2.5">
            <div className="space-y-2">
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Assigned Agent</span><span className="text-xs font-medium">{client.assigned_agent_name ? <Badge variant="outline" className="text-xs">{client.assigned_agent_name}</Badge> : <span className="text-orange-500 font-medium">Unassigned</span>}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Total Calls</span><span className="text-xs font-medium">{client.call_count || 0}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Last Call Status</span><Badge className="text-[10px] capitalize">{client.last_call_response ? client.last_call_response.replace(/_/g, " ") : "None"}</Badge></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Conversion</span><span className="text-xs font-medium capitalize">{client.conversion_status || "None"}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Last Updated</span><span className="text-xs text-muted-foreground">{client.updated_at ? client.updated_at.slice(0, 10) : "-"}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes / Remarks Callout if present */}
      {(client.last_call_notes || client.notes) && (
        <Card className="border border-border bg-muted/20">
          <CardContent className="p-4 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes & Remarks</p>
            {client.last_call_notes && <p className="text-xs text-foreground"><span className="font-medium text-muted-foreground">Call Remarks: </span>{client.last_call_notes}</p>}
            {client.notes && <p className="text-xs text-foreground"><span className="font-medium text-muted-foreground">Admin Notes: </span>{client.notes}</p>}
          </CardContent>
        </Card>
      )}

      {/* Call History */}
      <Card className="border border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" /> Call History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Agent</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Direction</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Response</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {call_history.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No call history</TableCell></TableRow>
              ) : call_history.map((call, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm">{call.created_at_display || call.created_at}</TableCell>
                  <TableCell className="text-sm">{call.agent_name || "N/A"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs capitalize">{call.direction}</Badge></TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs capitalize">{call.status}</Badge></TableCell>
                  <TableCell className="text-sm capitalize">{call.response || "-"}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{call.notes || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Call Response Modal */}
      <Dialog open={callModal && !convertModal} onOpenChange={setCallModal}>
        <DialogContent data-testid="detail-call-response-modal">
          <DialogHeader><DialogTitle>Call Response - {client.user_id}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {["interested", "not_interested", "follow_up", "not_connected", "converted"].map(r => (
                <Button key={r} variant={callResponse.response === r ? "default" : "outline"} size="sm" onClick={() => setCallResponse({ ...callResponse, response: r })} className="capitalize text-xs" data-testid={`detail-response-${r}`}>{r.replace(/_/g, " ")}</Button>
              ))}
            </div>
            <Textarea placeholder="Add remarks/notes..." value={callResponse.notes} onChange={e => setCallResponse({ ...callResponse, notes: e.target.value })} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallModal(false)}>Cancel</Button>
            <Button onClick={submitResponse} data-testid="detail-submit-response-btn">Save Response</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Converted Modal */}
      <Dialog open={convertModal} onOpenChange={setConvertModal}>
        <DialogContent data-testid="detail-convert-modal">
          <DialogHeader><DialogTitle>Conversion - {client.user_id}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Enter deposit details. This will be sent to admin for verification.</p>
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deposit Amount</Label><Input type="number" value={convertData.deposit_amount} onChange={e => setConvertData({ ...convertData, deposit_amount: e.target.value })} placeholder="Amount" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deposit Date</Label><Input type="date" value={convertData.deposit_date} onChange={e => setConvertData({ ...convertData, deposit_date: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertModal(false)}>Cancel</Button>
            <Button onClick={submitConversion}>Submit for Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Message Modal */}
      <Dialog open={waModal} onOpenChange={setWaModal}>
        <DialogContent data-testid="detail-wa-modal">
          <DialogHeader><DialogTitle>WhatsApp - {client.user_id}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Textarea value={waMsg} onChange={e => setWaMsg(e.target.value)} rows={4} placeholder="Message..." />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setWaModal(false)}>Cancel</Button><Button onClick={sendWhatsApp}>Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Modal */}
      {user?.role === "admin" && (
        <EditClientModal
          client={client}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSaveSuccess={(updated) => {
            if (updated && updated.user_id !== userId) {
              navigate(`/clients/${updated.user_id}`, { replace: true });
            } else {
              fetchData();
            }
          }}
        />
      )}
    </div>
  );
}
